import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Theme,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { FunctionComponent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { EUserRoles, IUser } from 'types/users'
import { useCreateExternalUserMutation } from 'services/users'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { zodResolver } from '@hookform/resolvers/zod'
import { createUserFormSchema } from '../validations/user-form-validation'

interface Props {
  handleClose: () => void
  roles: string[]
  organizationId?: string
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

function getStyles(name: string, selectedRoles: readonly string[], theme: Theme) {
  return {
    fontWeight:
      selectedRoles.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  }
}

export const NewUser: FunctionComponent<Props> = ({
  handleClose,
  roles,
  organizationId,
}) => {
  const { t } = useTranslation()
  const [createUser, { isLoading: isCreateUserLoading, isError, isSuccess }] =
    useCreateExternalUserMutation()
  const schema = createUserFormSchema(t)

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<IUser>({
    defaultValues: {
      active: true,
      firstName: '',
      lastName: '',
      personalIdentificationNumber: '',
      identityCardNumber: '',
      identityCardSeries: '',
      email: '',
      phoneNumber: '',
      countryCode: '',
      userRoles: [EUserRoles.PROCUREMENT_SPECIALIST],
      requiresEmailVerification: false,
      requiresPasswordReset: false,
    },
    resolver: zodResolver(schema),
  })
  const active = watch('active')

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
      handleClose()
    }
  }, [isSuccess, handleClose, t])

  const onSubmit = (values: IUser) => {
    createUser({ ...values, id: uuidv4(), organizationId })
  }

  return (
    <BootstrapDialog open={true} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
          {t('add-new-user-title')}
        </DialogTitle>

        <DialogCloseButton handleClose={handleClose} />

        <DialogContent dividers>
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            required
            label={t('name')}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.firstName}
            {...register('firstName')}
          />
          {errors.firstName && (
            <FormHelperText error>{errors.firstName?.message}</FormHelperText>
          )}
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            required
            label={<>{t('lastName')}</>}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.lastName}
            {...register('lastName')}
          />
          {errors.lastName && (
            <FormHelperText error>{errors.lastName?.message}</FormHelperText>
          )}
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            label={<>{t('position')}</>}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.position}
            {...register('position')}
          />
          {errors.position && (
            <FormHelperText error>{errors.position?.message}</FormHelperText>
          )}
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            required
            label={<>{t('fin')}</>}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.personalIdentificationNumber}
            {...register('personalIdentificationNumber')}
          />
          {errors.personalIdentificationNumber && (
            <FormHelperText error>
              {errors.personalIdentificationNumber?.message}
            </FormHelperText>
          )}
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            label={<>{t('phoneNumber')}</>}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.phoneNumber}
            {...register('phoneNumber')}
          />
          {errors.phoneNumber && (
            <FormHelperText error>{errors.phoneNumber?.message}</FormHelperText>
          )}
          <Box gap={2} display="flex">
            <Box width="100%">
              <TextField
                InputLabelProps={{ shrink: true }}
                size="small"
                label={<>{t('personalCardSeries')}</>}
                variant="outlined"
                margin="dense"
                sx={{ mr: 2 }}
                fullWidth
                error={!!errors.identityCardSeries}
                {...register('identityCardSeries')}
              />
              {errors.identityCardSeries && (
                <FormHelperText error>
                  {errors.identityCardSeries?.message}
                </FormHelperText>
              )}
            </Box>
            <Box width="100%">
              <TextField
                InputLabelProps={{ shrink: true }}
                size="small"
                label={<>{t('personalCardNumber')}</>}
                variant="outlined"
                margin="dense"
                fullWidth
                error={!!errors.identityCardNumber}
                {...register('identityCardNumber')}
              />
              {errors.identityCardNumber && (
                <FormHelperText error>
                  {errors.identityCardNumber?.message}
                </FormHelperText>
              )}
            </Box>
          </Box>
          <TextField
            InputLabelProps={{ shrink: true }}
            label={<>{t('country-code')}</>}
            size="small"
            margin="dense"
            variant="outlined"
            fullWidth
            error={!!errors.countryCode}
            {...register('countryCode')}
          />

          {errors.countryCode && (
            <FormHelperText error>{errors.countryCode?.message}</FormHelperText>
          )}
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            label={<>{t('email-address')}</>}
            variant="outlined"
            margin="dense"
            fullWidth
            error={!!errors.email}
            {...register('email')}
          />
          {errors.email && (
            <FormHelperText error>{errors.email?.message}</FormHelperText>
          )}

          <Box mt={1}>
            <Controller
              name="userRoles"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select fullWidth multiple value={value} onChange={onChange}>
                  {roles.map((role, index) => {
                    return (
                      <MenuItem key={index} value={role}>
                        {t(role.toLowerCase())}
                      </MenuItem>
                    )
                  })}
                </Select>
              )}
              //defaultValue="" // make sure to set up defaultValue
            />
            {errors.userRoles && (
              <FormHelperText error>{errors.userRoles?.message}</FormHelperText>
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                onChange={(e, c) => {
                  setValue('active', c)
                }}
              />
            }
            label={t('isActive')}
            checked={active}
            sx={{ display: 'block' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isCreateUserLoading}
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            autoFocus
            type="submit"
            sx={{ mr: 1 }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </form>
    </BootstrapDialog>
  )
}
